import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Platform, ViewStyle } from 'react-native';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import { PaymentSectionHeader } from './PaymentSectionHeader';
import { PaymentRadioOption } from './PaymentRadioOption';
import { PaymentAmountInput } from './PaymentAmountInput';
import { CurrencyInput } from './CurrencyInput';

const InfoIcon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE;

type Variant = 'desktop' | 'mobile';

interface PaymentAmountSectionProps {
  /** Whether custom amount is selected (vs balance due) */
  isCustomAmount: boolean;
  /** The balance due amount */
  balanceDue: number;
  /** Custom amount string value */
  customAmount: string;
  
  /** Handler for selecting balance due option */
  onSelectBalanceDue: () => void;
  /** Handler for selecting custom amount option */
  onSelectCustomAmount: () => void;
  /** Handler for custom amount text change */
  onCustomAmountChange: (text: string) => void;
  /** Handler for info icon press (mobile) */
  onInfoPress?: () => void;
  
  /** Display variant - affects styling */
  variant?: Variant;
  /** Display name for "on behalf of" header */
  displayName?: string;
  /** Whether balance is under minimum (special case) */
  canPayBalanceUnderMin?: boolean;
  /** Validation error message */
  validationError?: string;
  /** Whether the custom amount option is disabled */
  disabled?: boolean;
  /** Minimum payment amount for hint display */
  minimumAmount?: number;
  
  /** Custom label for balance due option */
  balanceLabel?: string;
  /** Custom label for other amount option */
  customLabel?: string;
  /** Tooltip text for info icon */
  tooltipText?: string;
  
  /** Custom container style */
  style?: ViewStyle;
  /** Ref for the custom amount TextInput (managed by parent via useAmountInput) */
  inputRef?: React.RefObject<TextInput | null>;
}

/**
 * Unified Payment Amount Section/Selector
 * Supports both mobile and desktop styling variants
 */
export const PaymentAmountSection: React.FC<PaymentAmountSectionProps> = ({
  isCustomAmount,
  balanceDue,
  customAmount,
  onSelectBalanceDue,
  onSelectCustomAmount,
  onCustomAmountChange,
  onInfoPress,
  variant = Platform.OS === 'web' ? 'desktop' : 'mobile',
  displayName,
  canPayBalanceUnderMin = false,
  validationError,
  disabled = false,
  minimumAmount,
  balanceLabel = 'Balance due',
  customLabel = 'Other amount',
  tooltipText = 'If the amount is more than the balance due, the overpaid amount will appear as a credit to the user\'s account and will be applied towards future charges when they are issued.',
  style,
  inputRef,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const DESKTOP_COLORS = useDesktopColors();
  const isMobile = variant === 'mobile';

  // Mobile variant rendering
  if (isMobile) {
    return (
      <View style={[styles.containerMobile, style]}>
        {/* Header text */}
        {displayName && (
          <Text style={[styles.headerTextMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>
            Make a payment on behalf of {displayName}
          </Text>
        )}

        {/* Balance Due Option */}
        <TouchableOpacity
          style={styles.optionRowMobile}
          onPress={onSelectBalanceDue}
          disabled={disabled}
          activeOpacity={0.7}
        >
          <View style={styles.optionLeftMobile}>
            <View style={styles.radioContainerMobile}>
              {!isCustomAmount ? (
                <View style={[styles.radioSelectedMobile, { backgroundColor: MARKETPLACE_COLORS.radioSelected }]}>
                  <View style={[styles.radioInnerMobile, { backgroundColor: MARKETPLACE_COLORS.textPrimary }]} />
                </View>
              ) : (
                <View style={[styles.radioUnselectedMobile, { backgroundColor: MARKETPLACE_COLORS.checkboxBackground, borderColor: MARKETPLACE_COLORS.checkboxBorder }]} />
              )}
            </View>
            <Text style={[styles.optionLabelMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>{balanceLabel}</Text>
          </View>
          <Text style={[styles.optionValueMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>{formatCurrency(balanceDue)}</Text>
        </TouchableOpacity>

        {/* Other Amount Option */}
        {!disabled && (
          <TouchableOpacity
            style={styles.optionRowMobile}
            onPress={onSelectCustomAmount}
            activeOpacity={0.7}
          >
            <View style={styles.optionLeftMobile}>
              <View style={styles.radioContainerMobile}>
                {isCustomAmount ? (
                  <View style={[styles.radioSelectedMobile, { backgroundColor: MARKETPLACE_COLORS.radioSelected }]}>
                    <View style={[styles.radioInnerMobile, { backgroundColor: MARKETPLACE_COLORS.textPrimary }]} />
                  </View>
                ) : (
                  <View style={[styles.radioUnselectedMobile, { backgroundColor: MARKETPLACE_COLORS.checkboxBackground, borderColor: MARKETPLACE_COLORS.checkboxBorder }]} />
                )}
              </View>
              <Text style={[styles.optionLabelMobile, { color: MARKETPLACE_COLORS.textPrimary }]}>{customLabel}</Text>
              {onInfoPress && InfoIcon && (
                <TouchableOpacity
                  style={styles.infoButtonMobile}
                  onPress={onInfoPress}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <InfoIcon width={20} height={20} color={MARKETPLACE_COLORS.textMuted} />
                </TouchableOpacity>
              )}
            </View>

            {/* Custom Amount Input */}
            <CurrencyInput
              ref={inputRef}
              containerStyle={StyleSheet.flatten([styles.amountInputContainerMobile, { backgroundColor: MARKETPLACE_COLORS.inputBackground, borderColor: MARKETPLACE_COLORS.inputBorder }])}
              currencySymbolStyle={StyleSheet.flatten([styles.currencySymbolMobile, { color: MARKETPLACE_COLORS.textSecondary }])}
              inputStyle={StyleSheet.flatten([styles.amountInputMobile, { color: MARKETPLACE_COLORS.textPrimary }])}
              hasError={!!validationError}
              errorContainerStyle={StyleSheet.flatten([styles.amountInputErrorMobile, { borderColor: MARKETPLACE_COLORS.accentRed }])}
              value={customAmount}
              onChangeText={onCustomAmountChange}
              placeholder={balanceDue.toFixed(2)}
              placeholderTextColor={MARKETPLACE_COLORS.textMuted}
              editable={isCustomAmount}
            />
          </TouchableOpacity>
        )}

        {/* Error message */}
        {validationError && (
          <Text style={[styles.errorTextMobile, { color: MARKETPLACE_COLORS.accentRed }]}>{validationError}</Text>
        )}

        {/* Minimum amount hint */}
        {minimumAmount && minimumAmount > 0 && (
          <Text style={[styles.hintTextMobile, { color: MARKETPLACE_COLORS.textMuted }]}>
            Minimum payment amount: {formatCurrency(minimumAmount)}
          </Text>
        )}
      </View>
    );
  }

  // Desktop variant rendering
  return (
    <View style={[styles.sectionDesktop, style]}>
      {Boolean(displayName) && (
        <PaymentSectionHeader 
          title={`Make a payment on behalf of ${displayName}`}
        />
      )}
      
      <PaymentRadioOption
        selected={!isCustomAmount}
        onPress={onSelectBalanceDue}
        label={balanceLabel}
        value={formatCurrency(balanceDue)}
      />

      <PaymentRadioOption
        selected={isCustomAmount}
        onPress={onSelectCustomAmount}
        label={customLabel}
        rightComponent={(
          <View style={styles.customAmountContainerDesktop}>
            {InfoIcon && (
              <View style={styles.tooltipContainerDesktop}>
                <InfoIcon width={18} height={18} color={DESKTOP_COLORS.TEXT_TERTIARY} />
              </View>
            )}
            <PaymentAmountInput
              value={customAmount}
              onChangeText={onCustomAmountChange}
              placeholder={canPayBalanceUnderMin ? String(balanceDue.toFixed(2)) : '0.00'}
              editable={isCustomAmount}
              disabled={!isCustomAmount || canPayBalanceUnderMin}
            />
          </View>
        )}
      />

      {!!validationError && (
        <Text style={styles.validationTextDesktop}>{validationError}</Text>
      )}
    </View>
  );
};

// Alias for mobile imports
export const PaymentAmountSelector = PaymentAmountSection;

const styles = StyleSheet.create({
  // Desktop styles
  sectionDesktop: {
    paddingVertical: 14,
  },
  customAmountContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tooltipContainerDesktop: {
    marginLeft: 8,
    marginRight: 8,
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  validationTextDesktop: {
    color: '#EF4444',
    fontSize: 12,
    marginTop: 6,
  },
  
  // Mobile styles
  containerMobile: {
    gap: 8,
  },
  headerTextMobile: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    marginBottom: 8,
  },
  optionRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 16,
    minHeight: 40,
  },
  optionLeftMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  radioContainerMobile: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelectedMobile: {
    width: 16,
    height: 16,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioInnerMobile: {
    width: 8,
    height: 8,
    borderRadius: 6,
  },
  radioUnselectedMobile: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
  },
  optionLabelMobile: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
    marginLeft: 6,
  },
  optionValueMobile: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  infoButtonMobile: {
    marginLeft: 6,
  },
  amountInputContainerMobile: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    height: 40,
    width: 128,
  },
  amountInputErrorMobile: {},
  currencySymbolMobile: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
  },
  amountInputMobile: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
    textAlign: 'right',
  },
  errorTextMobile: {
    fontSize: 12,
    fontWeight: '400',
    marginTop: 4,
    paddingLeft: 16,
  },
  hintTextMobile: {
    fontSize: 12,
    fontWeight: '400',
    marginTop: 4,
    paddingLeft: 16,
  },
});